
import {validate} from "@angular/forms/signals";

/** Specifies the quality features a valid password should have
 */
export interface PasswordQualityOptions {
    minLength: number;
    letters: boolean;
    maxLength?: number;
    mixedCase?: boolean;
    digits?: boolean;
    specialChars?: boolean;
    allowSpaces?: boolean;
}

interface PasswordQualityErrors {
    minLength?: string;
    letters?: string;
    maxLength?: string;
    mixedCase?: string;
    digits?: string;
    specialChars?: string;
    allowSpaces?: string;
}

/** Applies the validate function to compare two form fields and return null if they match,
 * or on mismatch an error object with kind 'mismatch' and a default message or the provided one in options, if any.
 *
 * @param field1 schema path to the first field to be compared
 * @param field2 schema path to the second field to be compared
 * @param options optional object containing a custom message to return in case of error
 */
export function fieldMatch(field1: any, field2: any, options?: {message?: string}): void {
    validate(field1, ({value, valueOf}) => {
        const v1 = value();
        const v2 = valueOf(field2);
        if (v1 !== v2)
            return {kind: 'mismatch', message: options?.message || "fields don't match"}
        return null;
    });
}

/** Applies the validate function to verify if the given form field satisfies the specified password quality options,
 * which returns null if all options are satisfied, or an error object specifying the kind and error message for
 * the first quality condition that was violated, from the list: minLength, letters, maxLength, mixedCase, digits,
 * specialChars, allowSpaces
 *
 * @param field schema path to the field to be validated
 * @param quality rules defining the requirements for the password
 * @param options optional object containing optional error messages for each of the checked conditions
 */
export function passwordQuality(field: any, quality: PasswordQualityOptions, options?: {messages?: PasswordQualityErrors}): void {
    validate<string>(field, ({value}) => {
        const v = value();
        if (quality.minLength && v.length < quality.minLength)
            return {kind: 'minLength', message: options?.messages?.minLength || `must be at least ${quality.minLength} characters long`};
        if (quality.letters && !v.match(/[a-zA-Z]/))
            return {kind: 'letters', message: options?.messages?.letters || "must contain letters"};
        if (quality.maxLength && v.length > quality.maxLength)
            return {kind: 'maxLength', message: options?.messages?.maxLength || `must be at most ${quality.maxLength} characters long`};
        if (quality.mixedCase && (!v.match(/[a-z]/) || !v.match(/[A-Z]/)))
            return {kind: 'mixedCase', message: options?.messages?.mixedCase || "must contain both uppercase and lowercase letters"};
        if (quality.digits && !v.match(/\d/))
            return {kind: 'digits', message: options?.messages?.digits || "must contain digits"};
        if (quality.specialChars && !v.match(/[^a-zA-Z0-9]/))
            return {kind: 'specialChars', message: options?.messages?.specialChars || "must contain special characters"};
        if (!quality.allowSpaces && v.match(/\s/))
            return {kind: 'allowSpaces', message: options?.messages?.allowSpaces || "must not contain spaces"};

        return null;
    });
}
